import React, { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { TabsSection } from "@/layout/components/tabs-section";
import {
	useSplitPanel,
	type Panel,
	type LayoutNode,
} from "@/contexts/split-panel-context";
import { useTabs } from "@/contexts/tabs-context";
import { PanelDropZones } from "@/components/drop-zone-overlay";
import { SplitPreview } from "@/components/split-indicator";
import { TabContentRenderer } from "@/components/tab-content-renderer";
import { cn } from "@/lib/utils";
import {
	useDndMonitor,
	DragOverlay,
	type DragOverEvent,
	type DragStartEvent,
	type DragEndEvent,
	type DragMoveEvent
} from "@dnd-kit/core";
import type { DropZonePosition } from "@/components/drop-zone-overlay";
import {
	Pin,
	MessagesSquare,
	FileText,
	Presentation,
	Video,
	Archive,
	KeyRound,
	Bot,
	FolderKanban,
} from "lucide-react";
import type { Tab, TabType } from "@/contexts/tabs-context";
import type { Channel, Workspace } from "@/utils/custom-types";
import { useWorkspaceContext } from "@/contexts/workspace-context";

// Helper function to get icon for tab type
const getTabIcon = (type: TabType) => {
	const icons: Record<TabType, typeof MessagesSquare> = {
		chat: MessagesSquare,
		doc: FileText,
		sheet: FileText,
		board: Presentation,
		meet: Video,
		storage: Archive,
		password: KeyRound,
		automation: Bot,
		project: FolderKanban,
		settings: MessagesSquare,
		more: MessagesSquare,
	};
	return icons[type] || MessagesSquare;
};

// ============================================================================
// PANEL CONTENT - Renders a single panel's content
// ============================================================================

interface PanelContentProps {
	panelId: string;
	size?: number;
	isFocused: boolean;
	isDragging: boolean;
	draggedTabId?: string | null;
	draggedChannelId?: string | null;
	isInsideAnyTabBar: boolean;
}

const PanelContent: React.FC<PanelContentProps> = ({
	panelId,
	isFocused,
	isDragging,
	draggedTabId,
	draggedChannelId,
	isInsideAnyTabBar,
}) => {
	const { getTabsByPanel, activeTabId, focusedTabId, getActiveTabForPanel, setActiveTabForPanel } = useTabs();
	const panelTabs = getTabsByPanel(panelId);

	// Get the active tab for this panel from TabsContext (single source of truth)
	let panelActiveTabId = getActiveTabForPanel(panelId);

	// Auto-select first tab if panel has tabs but no active tab set
	useEffect(() => {
		const currentActiveTabId = getActiveTabForPanel(panelId);
		
		if (panelTabs.length > 0 && !currentActiveTabId) {
			setActiveTabForPanel(panelId, panelTabs[0].id);
			return;
		}

		if (currentActiveTabId && !panelTabs.find((t) => t.id === currentActiveTabId)) {
			if (panelTabs.length > 0) {
				setActiveTabForPanel(panelId, panelTabs[0].id);
			} else {
				setActiveTabForPanel(panelId, null);
			}
		}
	}, [panelTabs, panelId, getActiveTabForPanel, setActiveTabForPanel]);

	// If this is the focused panel and global activeTabId is in this panel, use that
	if (isFocused && activeTabId) {
		const tabInPanel = panelTabs.find((t) => t.id === activeTabId);
		if (tabInPanel) {
			panelActiveTabId = activeTabId;
		}
	}

	const activeTab = panelTabs.find((t) => t.id === panelActiveTabId);

	const draggedChannelIdStr = draggedChannelId != null ? String(draggedChannelId) : null;
	const singleTabChannelIdStr = panelTabs[0]?.channelId != null ? String(panelTabs[0].channelId) : null;

	const hasNoTabs = panelTabs.length === 0;
	const hasOneTab = panelTabs.length === 1;
	
	const draggedTabBelongsToThisPanel = !!(draggedTabId && panelTabs.some(t => t.id === draggedTabId));
	const draggingSidebarChannel = !!draggedChannelIdStr;

	const hideBecauseNoTabs = draggingSidebarChannel && hasNoTabs;
	const hideBecauseSameSingleTab = draggingSidebarChannel && hasOneTab && singleTabChannelIdStr === draggedChannelIdStr;

	const shouldShowSplitZones =
		!hideBecauseNoTabs &&
		!hideBecauseSameSingleTab &&
		(!draggedTabBelongsToThisPanel || panelTabs.length > 1);

	return (
		<div
			className={cn(
				"relative flex h-full flex-col",
				!isFocused && "opacity-95",
			)}
			data-panel-id={panelId}
		>
			{!focusedTabId && (
				<TabsSection
					panelId={panelId}
					panelActiveTabId={panelActiveTabId}
					onTabClick={(tabId) => {
						setActiveTabForPanel(panelId, tabId);
					}}
					isInsideTabBar={isInsideAnyTabBar}
				/>
			)}
			<div className="relative flex-1 overflow-auto">
				{activeTab ? (
					<TabContentRenderer tab={activeTab} isFocused={isFocused} />
				) : (
					<div className="flex h-full items-center justify-center text-muted-foreground">
						<div className="text-center">
							<p className="text-sm">No tabs open in this panel</p>
							<p className="mt-2 text-xs">Drag a tab here or open a new one</p>
						</div>
					</div>
				)}
				<PanelDropZones
					panelId={panelId}
					isDragging={isDragging}
					showSplitZones={shouldShowSplitZones}
				/>
			</div>
		</div>
	);
};

// ============================================================================
// LAYOUT NODE RENDERER - Recursively renders the tree
// ============================================================================

interface LayoutNodeRendererProps {
	node: LayoutNode;
	isDragging: boolean;
	draggedTabId?: string | null;
	draggedChannelId?: string | null;
	isInsideAnyTabBar: boolean;
	onPanelClick: (panelId: string) => void;
	focusedPanelId: string;
}

const LayoutNodeRenderer: React.FC<LayoutNodeRendererProps> = ({
	node,
	isDragging,
	draggedTabId,
	draggedChannelId,
	isInsideAnyTabBar,
	onPanelClick,
	focusedPanelId,
}) => {
	const { setPanelSize } = useSplitPanel();

	// Render a panel
	if (node.type === "panel") {
		return (
			<div
				onClick={() => onPanelClick(node.id)}
				className={cn(
					"h-full w-full",
					node.id === focusedPanelId && "ring-2 ring-primary/20 ring-inset",
				)}
			>
				<PanelContent
					panelId={node.id}
					size={node.size}
					isFocused={node.id === focusedPanelId}
					isDragging={isDragging}
					draggedTabId={draggedTabId}
					draggedChannelId={draggedChannelId}
					isInsideAnyTabBar={isInsideAnyTabBar}
				/>
			</div>
		);
	}

	// Render a group with children
	const { children, orientation, id } = node;

	// Single child - no need for resizable container
	if (children.length === 1) {
		return (
			<div className="h-full w-full">
				<LayoutNodeRenderer
					node={children[0]}
					isDragging={isDragging}
					draggedTabId={draggedTabId}
					draggedChannelId={draggedChannelId}
					isInsideAnyTabBar={isInsideAnyTabBar}
					onPanelClick={onPanelClick}
					focusedPanelId={focusedPanelId}
				/>
			</div>
		);
	}

	// Multiple children - use resizable panel group
	return (
		<ResizablePanelGroup
			id={id}
			direction={orientation}
			className="h-full w-full"
		>
			{children.map((child, index) => (
				<React.Fragment key={child.id}>
					{index > 0 && <ResizableHandle withHandle />}
					<ResizablePanel
						id={child.id}
						order={index}
						defaultSize={child.size ?? 100 / children.length}
						minSize={20}
						onResize={(size) => {
							if (child.type === "panel") {
								setPanelSize(child.id, size);
							}
						}}
					>
						<LayoutNodeRenderer
							node={child}
							isDragging={isDragging}
							draggedTabId={draggedTabId}
							draggedChannelId={draggedChannelId}
							isInsideAnyTabBar={isInsideAnyTabBar}
							onPanelClick={onPanelClick}
							focusedPanelId={focusedPanelId}
						/>
					</ResizablePanel>
				</React.Fragment>
			))}
		</ResizablePanelGroup>
	);
};

// ============================================================================
// MAIN CONTAINER
// ============================================================================

export const SplitPanelContainer = ({
	currentWorkspace,
}: {
	currentWorkspace: Workspace;
}) => {
	const {
		layout,
		focusedPanelId,
		removePanel,
		splitPanelWithDrop,
		getAllPanels,
		focusPanel,
		resetLayout,
		getPanelById,
	} = useSplitPanel();
	const {
		getTabsByPanel,
		moveTabToPanel,
		moveTabToPanelLastIndex,
		setActiveTab,
		tabs: allTabs,
		reorderTabs,
		openPermanentTab,
		createTabDataFromChannel,
		getActiveTabForPanel,
	} = useTabs();

	const navigate = useNavigate();
	const { panelStateLoading, tabStateLoading } = useWorkspaceContext();

	const [isDragging, setIsDragging] = useState(false);
	const [draggedTabId, setDraggedTabId] = useState<string | null>(null);
	const [draggedChannelId, setDraggedChannelId] = useState<string | null>(null);
	const [activeDropZone, setActiveDropZone] = useState<{
		panelId: string;
		position: DropZonePosition;
	} | null>(null);
	const [dropPreviewRect, setDropPreviewRect] = useState<DOMRect | null>(null);
	const [isInsideAnyTabBar, setIsInsideAnyTabBar] = useState(true);

	function pointerInsideAnyTabBar(x: number, y: number) {
		const nodes = document.querySelectorAll<HTMLElement>('[data-tabs-section="true"]');
		for (const n of Array.from(nodes)) {
			const r = n.getBoundingClientRect();
			if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
				return true;
			}
		}
		return false;
	}

	// Handle panel focus on click
	const handlePanelClick = (panelId: string) => {
		if (panelId !== focusedPanelId) {
			focusPanel(panelId);

			const activeTabIdForPanel = getActiveTabForPanel(panelId);
			if (activeTabIdForPanel) {
				const panelTabs = getTabsByPanel(panelId);
				const activeTab = panelTabs.find((t) => t.id === activeTabIdForPanel);
				if (activeTab) {
					setActiveTab(activeTab.id);
					navigate({ to: activeTab.path as any });
				}
			} else {
				const panelTabs = getTabsByPanel(panelId);
				if (panelTabs.length > 0) {
					const firstTab = panelTabs[0];
					setActiveTab(firstTab.id);
					navigate({ to: firstTab.path as any });
				}
			}
		}
	};

	// Centralized drag event monitoring
	useDndMonitor({
		onDragStart: (event: DragStartEvent) => {
			setIsDragging(true);
			const draggedTab = allTabs.find((tab) => tab.id === event.active.id);
			if (draggedTab) {
				setDraggedTabId(draggedTab.id);
				setDraggedChannelId(draggedTab.channelId);
			} else {
				const channel = event.active.data?.current?.channel;
				setDraggedChannelId(channel?.id);
			}
		},
		onDragMove: (event: DragMoveEvent) => {
			const e = event.activatorEvent as MouseEvent | PointerEvent | undefined;
			if (!e) return;
			const x = e.clientX + event.delta.x;
			const y = e.clientY + event.delta.y;
			setIsInsideAnyTabBar(pointerInsideAnyTabBar(x, y));
		},
		onDragOver: (event: DragOverEvent) => {
			const { over } = event;

			if (!over || !over.data.current) {
				setActiveDropZone(null);
				setDropPreviewRect(null);
				return;
			}

			const data = over.data.current;

			if (data.type === "split-zone") {
				const panelElement = document.querySelector(
					`[data-panel-id="${data.panelId}"]`,
				) as HTMLElement;

				if (panelElement) {
					setActiveDropZone({
						panelId: data.panelId,
						position: data.position as DropZonePosition,
					});
					setDropPreviewRect(panelElement.getBoundingClientRect());
				}
			} else {
				setActiveDropZone(null);
				setDropPreviewRect(null);
			}
		},
		onDragEnd: (event: DragEndEvent) => {
			const { active, over } = event;

			const channel = event.active.data?.current?.channel as Channel | undefined;
			if (channel) {
				const workspaceId = currentWorkspace.id;
				const tabData = createTabDataFromChannel(workspaceId, channel);

				if (over?.data?.current?.type === "tabs-section") {
					const targetPanelId = over.data.current.panelId as string;
					openPermanentTab(tabData, targetPanelId);
					focusPanel(targetPanelId);
				} else if (over?.data?.current?.type === "split-zone") {
					const dropPanelId = over.data.current.panelId as string;
					const dropPosition = over.data.current.position as DropZonePosition;

					if (dropPosition === "center") {
						openPermanentTab(tabData, dropPanelId);
						focusPanel(dropPanelId);
					} else {
						const newPanelId = splitPanelWithDrop(dropPanelId, dropPosition, {
							...tabData,
							id: "temp",
							isPinned: false,
							isTemporary: true,
							lastVisited: new Date(),
						} as any);
						openPermanentTab(tabData, newPanelId);
						focusPanel(newPanelId);
					}
				}
			}

			const draggedTab = allTabs.find((tab) => tab.id === active.id);
			const sourcePanelId = draggedTab?.panelId;

			if (draggedTab && over) {
				const targetTab = allTabs.find((tab) => tab.id === over.id);
				if (targetTab && draggedTab.panelId === targetTab.panelId) {
					const panelTabs = getTabsByPanel(draggedTab.panelId || "default");

					const sortedTabs = [...panelTabs].sort((a, b) => {
						if (a.isPinned && !b.isPinned) return -1;
						if (!a.isPinned && b.isPinned) return 1;
						return 0;
					});

					const oldIndex = sortedTabs.findIndex((tab) => tab.id === draggedTab.id);
					const newIndex = sortedTabs.findIndex((tab) => tab.id === targetTab.id);

					if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
						reorderTabs(oldIndex, newIndex, draggedTab.panelId || "default");
					}
				} else if (over.data.current?.type === "tabs-section") {
					const targetPanelId = over.data.current.panelId;
					moveTabToPanelLastIndex(draggedTab.id, targetPanelId);
					focusPanel(targetPanelId);
					setActiveTab(draggedTab.id);

					if (sourcePanelId && sourcePanelId !== targetPanelId) {
						const sourcePanelTabs = getTabsByPanel(sourcePanelId);
						if (sourcePanelTabs.length === 0 && getAllPanels().length > 1) {
							removePanel(sourcePanelId);
						}
					}
				} else if (over.data.current?.type === "split-zone") {
					const dropPanelId = over.data.current.panelId;
					const dropPosition = over.data.current.position as DropZonePosition;

					if (dropPosition === "center") {
						moveTabToPanel(draggedTab.id, dropPanelId);
						focusPanel(dropPanelId);
						setActiveTab(draggedTab.id);

						if (sourcePanelId && sourcePanelId !== dropPanelId) {
							const sourcePanelTabs = getTabsByPanel(sourcePanelId);
							if (sourcePanelTabs.length === 0 && getAllPanels().length > 1) {
								removePanel(sourcePanelId);
							}
						}
					} else {
						const newPanelId = splitPanelWithDrop(dropPanelId, dropPosition, draggedTab);
						moveTabToPanel(draggedTab.id, newPanelId);
						focusPanel(newPanelId);
						setActiveTab(draggedTab.id);

						if (sourcePanelId && sourcePanelId !== newPanelId) {
							const sourcePanelTabs = getTabsByPanel(sourcePanelId);
							if (sourcePanelTabs.length === 0 && getAllPanels().length > 1) {
								removePanel(sourcePanelId);
							}
						}
					}
				}
			}

			setIsDragging(false);
			setDraggedTabId(null);
			setDraggedChannelId(null);
			setActiveDropZone(null);
			setDropPreviewRect(null);
			setIsInsideAnyTabBar(true);
		},
		onDragCancel: () => {
			setIsDragging(false);
			setDraggedTabId(null);
			setDraggedChannelId(null);
			setActiveDropZone(null);
			setDropPreviewRect(null);
			setIsInsideAnyTabBar(true);
		},
	});

	// Clean up empty panels
	useEffect(() => {
		if (isDragging || panelStateLoading || tabStateLoading) return;

		const panels = getAllPanels();
		panels.forEach((panel) => {
			const panelTabs = getTabsByPanel(panel.id);
			if (panelTabs.length === 0 && panels.length > 1) {
				removePanel(panel.id);
			}
		});
	}, [getAllPanels, getTabsByPanel, removePanel, isDragging, panelStateLoading, tabStateLoading]);

	// Handle keyboard shortcuts for panel management
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.altKey && e.key === "ArrowRight") {
				e.preventDefault();
				const panel = getPanelById(focusedPanelId);
				if (panel) {
					const tabs = getTabsByPanel(focusedPanelId);
					const activeTabId = getActiveTabForPanel(focusedPanelId);
					if (tabs.length > 0 && activeTabId) {
						const activeTab = tabs.find((t) => t.id === activeTabId);
						if (activeTab) splitPanelWithDrop(focusedPanelId, "right", activeTab);
					}
				}
			}

			if ((e.ctrlKey || e.metaKey) && e.altKey && e.key === "ArrowLeft") {
				e.preventDefault();
				const panel = getPanelById(focusedPanelId);
				if (panel) {
					const tabs = getTabsByPanel(focusedPanelId);
					const activeTabId = getActiveTabForPanel(focusedPanelId);
					if (tabs.length > 0 && activeTabId) {
						const activeTab = tabs.find((t) => t.id === activeTabId);
						if (activeTab) splitPanelWithDrop(focusedPanelId, "left", activeTab);
					}
				}
			}

			if ((e.ctrlKey || e.metaKey) && e.altKey && e.key === "ArrowDown") {
				e.preventDefault();
				const panel = getPanelById(focusedPanelId);
				if (panel) {
					const tabs = getTabsByPanel(focusedPanelId);
					const activeTabId = getActiveTabForPanel(focusedPanelId);
					if (tabs.length > 0 && activeTabId) {
						const activeTab = tabs.find((t) => t.id === activeTabId);
						if (activeTab) splitPanelWithDrop(focusedPanelId, "bottom", activeTab);
					}
				}
			}

			if ((e.ctrlKey || e.metaKey) && e.altKey && e.key === "ArrowUp") {
				e.preventDefault();
				const panel = getPanelById(focusedPanelId);
				if (panel) {
					const tabs = getTabsByPanel(focusedPanelId);
					const activeTabId = getActiveTabForPanel(focusedPanelId);
					if (tabs.length > 0 && activeTabId) {
						const activeTab = tabs.find((t) => t.id === activeTabId);
						if (activeTab) splitPanelWithDrop(focusedPanelId, "top", activeTab);
					}
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [focusedPanelId, getPanelById, getTabsByPanel, getActiveTabForPanel, splitPanelWithDrop]);

	// Loading state
	if (panelStateLoading || tabStateLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
			</div>
		);
	}

	// Validate layout
	if (!layout.root) {
		console.error("Layout root is missing, resetting...");
		resetLayout();
		return (
			<div className="flex h-full items-center justify-center text-muted-foreground">
				<div className="text-center">
					<p className="text-sm">Layout corrupted, resetting...</p>
					<p className="text-xs mt-2">Please refresh if this persists</p>
				</div>
			</div>
		);
	}

	const activeDragTab = draggedTabId
		? allTabs.find((tab) => tab.id === draggedTabId)
		: null;

	return (
		<div className="relative h-full">
			<LayoutNodeRenderer
				node={layout.root}
				isDragging={isDragging}
				draggedTabId={draggedTabId}
				draggedChannelId={draggedChannelId}
				isInsideAnyTabBar={isInsideAnyTabBar}
				onPanelClick={handlePanelClick}
				focusedPanelId={focusedPanelId}
			/>

			{activeDropZone && dropPreviewRect && (
				<SplitPreview
					position={activeDropZone.position}
					isVisible={true}
					containerRect={dropPreviewRect}
				/>
			)}

			{activeDragTab && !isInsideAnyTabBar && (
				<DragOverlay>
					<div
						className={cn(
							"flex h-full min-w-0 cursor-pointer items-center gap-2 border px-3 py-1 shadow-2xl opacity-30 scale-105",
							"border-border bg-card text-card-foreground",
							activeDragTab.isTemporary && "opacity-75",
						)}
					>
						{activeDragTab.isPinned && (
							<Pin className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
						)}
						{(() => {
							const Icon = getTabIcon(activeDragTab.type);
							return (
								<Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
							);
						})()}
						<span
							className={cn(
								"max-w-[120px] truncate text-xs",
								activeDragTab.isTemporary && "italic",
							)}
						>
							{activeDragTab.name}
						</span>
					</div>
				</DragOverlay>
			)}
		</div>
	);
};
